/**
 * Google Autocomplete Core
 * Pure functions for Google Places API integration
 */

export interface GooglePrediction {
    place_id: string;
    description: string;
    structured_formatting?: {
        main_text: string;
        secondary_text: string;
    };
}

export interface GooglePlaceDetails {
    place_id: string;
    formatted_address: string;
    address_components: {
        long_name: string;
        short_name: string;
        types: string[];
    }[];
    geometry: {
        location: {
            lat: number;
            lng: number;
        };
    };
}

export interface AddressComponents {
    streetNumber?: string;
    route?: string;
    city?: string;
    state?: string;
    postalCode?: string;
    country?: string;
    countryCode?: string;
    locality?: string;
    administrativeAreaLevel1?: string;
    administrativeAreaLevel1Long?: string;
    administrativeAreaLevel2?: string;
    administrativeAreaLevel2Long?: string;
    iso?: string;
}

export class GoogleAutocompleteCore {
    private static apiKey: string | null = null;
    private static service: any = null;

    /**
     * Initialize Google Places API
     */
    static initialize(apiKey: string): void {
        this.apiKey = apiKey;

        if (typeof window !== 'undefined' && window.google?.maps?.places) {
            this.service = new window.google.maps.places.AutocompleteService();
        }
    }

    /**
     * Search for places
     */
    static async searchPlaces(
        input: string,
        options: {
            types?: string[];
            componentRestrictions?: { country: string };
        } = {}
    ): Promise<GooglePrediction[]> {
        if (!this.service || !input.trim()) {
            return [];
        }

        return new Promise((resolve, reject) => {
            this.service.getPlacePredictions(
                {
                    input,
                    types: options.types || ['address'],
                    componentRestrictions: options.componentRestrictions,
                },
                (predictions: GooglePrediction[] | null, status: any) => {
                    if (status === window.google?.maps?.places?.PlacesServiceStatus.OK && predictions) {
                        resolve(predictions);
                    } else {
                        resolve([]);
                    }
                }
            );
        });
    }

    /**
     * Get place details
     */
    static async getPlaceDetails(
        placeId: string
    ): Promise<GooglePlaceDetails | null> {
        if (typeof window === 'undefined' || !window.google?.maps?.places) {
            return null;
        }

        return new Promise((resolve, reject) => {
            const service = new window.google!.maps!.places!.PlacesService(
                document.createElement('div')
            );

            service.getDetails(
                {
                    placeId,
                    fields: ['place_id', 'formatted_address', 'address_components', 'geometry'],
                },
                (place: GooglePlaceDetails | null, status: any) => {
                    if (status === window.google?.maps?.places?.PlacesServiceStatus.OK && place) {
                        resolve(place);
                    } else {
                        resolve(null);
                    }
                }
            );
        });
    }

    /**
     * Extract address components from place details
     */
    static extractAddressComponents(placeDetails: GooglePlaceDetails): AddressComponents {
        const components: AddressComponents = {};

        for (const component of placeDetails.address_components) {
            const types = component.types;

            if (types.includes('street_number')) {
                components.streetNumber = component.long_name;
            } else if (types.includes('route')) {
                components.route = component.long_name;
            } else if (types.includes('locality')) {
                components.city = component.long_name;
                components.locality = component.long_name;
            } else if (types.includes('administrative_area_level_2')) {
                components.administrativeAreaLevel2 = component.short_name;
                components.administrativeAreaLevel2Long = component.long_name;
                // Use level_2 as fallback for city if city is not set
                if (!components.city) {
                    components.city = component.long_name;
                    components.locality = component.long_name;
                }
            } else if (types.includes('administrative_area_level_1')) {
                components.state = component.long_name;
                components.administrativeAreaLevel1 = component.short_name;
                components.administrativeAreaLevel1Long = component.long_name;
            } else if (types.includes('postal_code')) {
                components.postalCode = component.long_name;
            } else if (types.includes('country')) {
                components.country = component.long_name;
                components.countryCode = component.short_name;
                components.iso = component.short_name;
            }
        }

        // For countries like France where administrative_area_level_1 (région) may be missing,
        // use administrative_area_level_2 (département) as the primary state/province value
        // We prefer the long_name (e.g., "Bouches-du-Rhône") over short_name (e.g., "13")
        // because it's more likely to match our state database entries
        if (!components.administrativeAreaLevel1 && components.administrativeAreaLevel2) {
            // Use long name as the primary state value (e.g., "Bouches-du-Rhône" instead of "13")
            components.state = components.administrativeAreaLevel2Long || components.administrativeAreaLevel2;
            components.administrativeAreaLevel1 = components.administrativeAreaLevel2;
            components.administrativeAreaLevel1Long = components.administrativeAreaLevel2Long;
        }

        return components;
    }
}
